/**
 * DateFormatter - A utility class for formatting UTC dates using Intl API
 * Handles common date/time display formats for frontend applications
 * // Usage examples:
 * /*
 * import { DateFormatter } from './dateFormatter';
 *
 * // Using static methods
 * const utcDate = '2024-01-15T14:30:00.000Z';
 * console.log(DateFormatter.short(utcDate));        // "1/15/24"
 * console.log(DateFormatter.medium(utcDate));       // "Jan 15, 2024"
 * console.log(DateFormatter.long(utcDate));         // "January 15, 2024"
 * console.log(DateFormatter.dateTime12(utcDate));   // "Jan 15, 2024, 2:30 PM"
 * console.log(DateFormatter.relative(utcDate));     // "5 months ago"
 * console.log(DateFormatter.smart(utcDate));        // Smart formatting
 *
 * // Using custom locale/timezone
 * console.log(DateFormatter.long(utcDate, 'de-DE', 'Europe/Berlin')); // "15. Januar 2024"
 * console.log(DateFormatter.time24(utcDate, 'en-GB', 'Europe/London')); // "14:30"
 *
 * // React component example
 * const DateDisplay = ({ utcDate, format = 'smart', locale, timeZone }) => {
 *   if (!utcDate) return null;
 *
 *   try {
 *     return <span>{DateFormatter[format](utcDate, locale, timeZone)}</span>;
 *   } catch (error) {
 *     console.error('Date formatting error:', error);
 *     return <span>Invalid date</span>;
 *   }
 * };
 * */

class DateFormatter {
    static defaultLocale = 'en-US';
    static defaultTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    /**
     * Parse UTC date string or Date object
     * @param {string|Date} utcDate - UTC date string or Date object
     * @returns {Date} - Date object
     */
    static parseDate(utcDate) {
        if (!utcDate) return null;

        if (utcDate instanceof Date) {
            return utcDate;
        }

        // Handle various UTC string formats
        const date = new Date(utcDate);
        if (isNaN(date.getTime())) {
            throw new Error(`Invalid date: ${utcDate}`);
        }

        return date;
    }

    /**
     * Format date with custom Intl options
     * @param {string|Date} utcDate - UTC date
     * @param {object} options - Intl.DateTimeFormat options
     * @param {string} locale - Locale override
     * @param {string} timeZone - TimeZone override
     * @returns {string} - Formatted date string
     */
    static format(utcDate, options = {}, locale = DateFormatter.defaultLocale, timeZone = DateFormatter.defaultTimeZone) {
        const date = DateFormatter.parseDate(utcDate);
        if (!date) return '';

        const defaultOptions = {
            timeZone,
            ...options
        };

        return new Intl.DateTimeFormat(locale, defaultOptions).format(date);
    }

    // Common date formats
    static short(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        }, locale, timeZone);
    }

    static medium(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            month: 'short',
            day: '2-digit',
            year: 'numeric'
        }, locale, timeZone);
    }

    static long(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            month: 'long',
            day: '2-digit',
            year: 'numeric'
        }, locale, timeZone);
    }

    static full(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            weekday: 'long',
            month: 'long',
            day: '2-digit',
            year: 'numeric'
        }, locale, timeZone);
    }

    // Time formats
    static time12(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }, locale, timeZone);
    }

    static time24(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }, locale, timeZone);
    }

    static timeWithSeconds(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        }, locale, timeZone);
    }

    // Combined date and time formats
    static dateTime12(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }, locale, timeZone);
    }

    static dateTime24(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }, locale, timeZone);
    }

    static fullDateTime(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }, locale, timeZone);
    }

    // Relative time formatting
    static relative(utcDate, locale = DateFormatter.defaultLocale) {
        const date = DateFormatter.parseDate(utcDate);
        if (!date) return '';

        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        const now = new Date();
        const diffInSeconds = Math.floor((date - now) / 1000);

        const intervals = [
            { unit: 'year', seconds: 31536000 },
            { unit: 'month', seconds: 2592000 },
            { unit: 'week', seconds: 604800 },
            { unit: 'day', seconds: 86400 },
            { unit: 'hour', seconds: 3600 },
            { unit: 'minute', seconds: 60 },
            { unit: 'second', seconds: 1 }
        ];

        for (const interval of intervals) {
            const count = Math.floor(Math.abs(diffInSeconds) / interval.seconds);
            if (count >= 1) {
                return rtf.format(diffInSeconds < 0 ? -count : count, interval.unit);
            }
        }

        return rtf.format(0, 'second');
    }

    // Specialized formats
    static iso(utcDate) {
        const date = DateFormatter.parseDate(utcDate);
        return date ? date.toISOString() : '';
    }

    static monthYear(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            month: 'long',
            year: 'numeric'
        }, locale, timeZone);
    }

    static dayOfWeek(utcDate, locale, timeZone) {
        return DateFormatter.format(utcDate, {
            weekday: 'long'
        }, locale, timeZone);
    }

    // Utility methods
    static isToday(utcDate, locale, timeZone) {
        const date = DateFormatter.parseDate(utcDate);
        if (!date) return false;

        const today = new Date();
        const dateStr = DateFormatter.format(date, { year: 'numeric', month: '2-digit', day: '2-digit' }, locale, timeZone);
        const todayStr = DateFormatter.format(today, { year: 'numeric', month: '2-digit', day: '2-digit' }, locale, timeZone);

        return dateStr === todayStr;
    }

    static isYesterday(utcDate, locale, timeZone) {
        const date = DateFormatter.parseDate(utcDate);
        if (!date) return false;

        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);

        const dateStr = DateFormatter.format(date, { year: 'numeric', month: '2-digit', day: '2-digit' }, locale, timeZone);
        const yesterdayStr = DateFormatter.format(yesterday, { year: 'numeric', month: '2-digit', day: '2-digit' }, locale, timeZone);

        return dateStr === yesterdayStr;
    }

    // Smart formatting - shows relative for recent dates, absolute for older
    static smart(utcDate, locale, timeZone) {
        const date = DateFormatter.parseDate(utcDate);
        if (!date) return '';

        if (DateFormatter.isToday(date, locale, timeZone)) {
            return `Today, ${DateFormatter.time12(date, locale, timeZone)}`;
        }

        if (DateFormatter.isYesterday(date, locale, timeZone)) {
            return `Yesterday, ${DateFormatter.time12(date, locale, timeZone)}`;
        }

        const daysDiff = Math.abs(Math.floor((new Date() - date) / (1000 * 60 * 60 * 24)));

        if (daysDiff <= 7) {
            return `${DateFormatter.dayOfWeek(date, locale, timeZone)}, ${DateFormatter.time12(date, locale, timeZone)}`;
        }

        if (daysDiff <= 365) {
            return DateFormatter.dateTime12(date, locale, timeZone);
        }

        return DateFormatter.medium(date, locale, timeZone);
    }
}

// Export the class
export { DateFormatter };

